import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import { updateUser } from '../store/authSlice';
import './AccountPage.css';

const AccountPage = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [profileData, setProfileData] = useState({
    first_name: '', last_name: '', email: '', phone: '', username: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '', new_password: '', confirm_password: ''
  });

  const [addresses, setAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({
    address_line1: '', address_line2: '', city: '', state: '',
    postal_code: '', country: 'India', is_default: false
  });
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name:  user.last_name  || '',
        email:      user.email      || '',
        phone:      user.phone      || '',
        username:   user.username   || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchAddresses();
      fetchOrders();
    }
  }, [isAuthenticated, user]);

  // Helper — make sure items is always an array
  const parseItems = (items) => {
    if (Array.isArray(items)) return items;
    if (typeof items === 'string') {
      try { const parsed = JSON.parse(items); return Array.isArray(parsed) ? parsed : []; }
      catch { return []; }
    }
    return [];
  };

  const fetchAddresses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/addresses`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setAddresses(Array.isArray(data.data) ? data.data : []);
        setErrorMessage('');
      } else {
        setAddresses([]);
        setErrorMessage(data.message || 'Failed to fetch addresses');
      }
    } catch (error) {
      setAddresses([]);
      setErrorMessage('Network error: ' + error.message);
    }
  };

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/orders`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        // Normalise every order's items field to an array right here
        const normalised = (Array.isArray(data.data) ? data.data : []).map(order => ({
          ...order,
          items: parseItems(order.items)
        }));
        setOrders(normalised);
        setErrorMessage('');
      } else {
        setOrders([]);
        setErrorMessage(data.message || 'Failed to fetch orders');
      }
    } catch (error) {
      setOrders([]);
      setErrorMessage('Network error: ' + error.message);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setSuccessMessage(''); setErrorMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/users/${user.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          first_name: profileData.first_name, 
          last_name: profileData.last_name, 
          phone: profileData.phone 
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccessMessage('Profile updated successfully!');
        dispatch(updateUser({ 
          ...user, 
          first_name: profileData.first_name, 
          last_name: profileData.last_name, 
          phone: profileData.phone 
        }));
      } else { 
        setErrorMessage(data.message || 'Failed to update profile'); 
      }
    } catch (error) { 
      setErrorMessage('An error occurred. Please try again.'); 
    } finally { 
      setLoading(false); 
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) { 
      setErrorMessage('New passwords do not match'); 
      return; 
    }
    if (passwordData.new_password.length < 6) { 
      setErrorMessage('Password must be at least 6 characters'); 
      return; 
    }
    setLoading(true); setSuccessMessage(''); setErrorMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/password`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          current_password: passwordData.current_password, 
          new_password: passwordData.new_password 
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccessMessage('Password changed successfully!');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else { 
        setErrorMessage(data.message || 'Failed to change password'); 
      }
    } catch (error) { 
      setErrorMessage('An error occurred. Please try again.'); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setSuccessMessage(''); setErrorMessage('');
    try {
      const url = editingAddressId
        ? `${API_BASE_URL}/users/${user.id}/addresses/${editingAddressId}`
        : `${API_BASE_URL}/users/${user.id}/addresses`;
      
      const response = await fetch(url, {
        method: editingAddressId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newAddress)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccessMessage(editingAddressId ? 'Address updated successfully!' : 'Address added successfully!');
        setNewAddress({ 
          address_line1: '', address_line2: '', city: '', state: '', 
          postal_code: '', country: 'India', is_default: false 
        });
        setShowAddressForm(false); 
        setEditingAddressId(null);
        fetchAddresses();
      } else { 
        setErrorMessage(data.message || 'Failed to save address'); 
      }
    } catch (error) { 
      setErrorMessage('An error occurred. Please try again.'); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/addresses/${addressId}`, { 
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok && data.success) { 
        setSuccessMessage('Address deleted successfully!'); 
        fetchAddresses(); 
      } else { 
        setErrorMessage(data.message || 'Failed to delete address'); 
      }
    } catch (error) { 
      setErrorMessage('An error occurred. Please try again.'); 
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/addresses/${addressId}/default`, { 
        method: 'PUT',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (response.ok && data.success) { 
        setSuccessMessage('Default address updated!'); 
        fetchAddresses(); 
      } else { 
        setErrorMessage(data.message || 'Failed to set default address'); 
      }
    } catch (error) { 
      setErrorMessage('An error occurred. Please try again.'); 
    }
  };

  const getStatusColor = (status) => {
    const colors = { 
      pending: '#f39c12', 
      processing: '#3498db', 
      shipped: '#9b59b6', 
      delivered: '#27ae60', 
      cancelled: '#e74c3c' 
    };
    return colors[status] || '#95a5a6';
  };

  const editAddress = (address) => {
    setNewAddress({
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || 'India',
      is_default: address.is_default || false
    });
    setEditingAddressId(address.id);
    setShowAddressForm(true);
  };

  if (!user) return <div className="account-page loading">Loading...</div>;

  return (
    <div className="account-page">
      <div className="container">
        <div className="account-header">
          <h1 className="account-title">
            <span className="title-line">My</span>
            <span className="title-line highlight">Account</span>
          </h1>
          <p className="account-subtitle">Manage your profile, addresses, and orders</p>
        </div>

        {successMessage && (
          <div className="alert alert-success">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M8 12l3 3 6-6"/>
            </svg>
            <span>{successMessage}</span>
            <button className="alert-close" onClick={() => setSuccessMessage('')}>×</button>
          </div>
        )}

        {errorMessage && (
          <div className="alert alert-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{errorMessage}</span>
            <button className="alert-close" onClick={() => setErrorMessage('')}>×</button>
          </div>
        )}

        <div className="account-tabs">
          {[
            { key: 'profile',   label: 'Profile',   icon: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></> },
            { key: 'addresses', label: 'Addresses', icon: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></> },
            { key: 'orders',    label: 'Orders',    icon: <><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h4"/></> },
            { key: 'password',  label: 'Security',  icon: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></> },
          ].map(({ key, label, icon }) => (
            <button key={key} className={`tab-btn ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{icon}</svg>
              {label}
            </button>
          ))}
        </div>

        <div className="tab-content">

          {/* ── PROFILE ── */}
          {activeTab === 'profile' && (
            <div className="profile-tab">
              <div className="profile-header">
                <div className="profile-avatar">
                  <span className="avatar-initials">{user.first_name?.charAt(0)}{user.last_name?.charAt(0)}</span>
                </div>
                <div className="profile-info">
                  <h2>{user.first_name} {user.last_name}</h2>
                  <p>Member since {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
              <form onSubmit={handleProfileSubmit} className="profile-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input type="text" value={profileData.first_name} onChange={(e) => setProfileData({...profileData, first_name: e.target.value})} className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input type="text" value={profileData.last_name} onChange={(e) => setProfileData({...profileData, last_name: e.target.value})} className="form-input" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Username</label>
                  <input type="text" value={profileData.username} disabled className="form-input disabled" />
                  <small>Username cannot be changed</small>
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input type="email" value={profileData.email} disabled className="form-input disabled" />
                  <small>Email cannot be changed</small>
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input type="tel" value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} className="form-input" placeholder="Enter your phone number" />
                </div>
                <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
              </form>
            </div>
          )}

          {/* ── ADDRESSES ── */}
          {activeTab === 'addresses' && (
            <div className="addresses-tab">
              <div className="addresses-header">
                <h3>Saved Addresses</h3>
                <button className="btn-secondary" onClick={() => { 
                  setNewAddress({ address_line1: '', address_line2: '', city: '', state: '', postal_code: '', country: 'India', is_default: false }); 
                  setEditingAddressId(null); 
                  setShowAddressForm(!showAddressForm); 
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add New Address
                </button>
              </div>

              {showAddressForm && (
                <form onSubmit={handleAddressSubmit} className="address-form">
                  <h4>{editingAddressId ? 'Edit Address' : 'Add New Address'}</h4>
                  <div className="form-group">
                    <label>Address Line 1 *</label>
                    <input type="text" value={newAddress.address_line1} onChange={(e) => setNewAddress({...newAddress, address_line1: e.target.value})} required className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input type="text" value={newAddress.address_line2} onChange={(e) => setNewAddress({...newAddress, address_line2: e.target.value})} className="form-input" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input type="text" value={newAddress.city} onChange={(e) => setNewAddress({...newAddress, city: e.target.value})} required className="form-input" />
                    </div>
                    <div className="form-group">
                      <label>State *</label>
                      <input type="text" value={newAddress.state} onChange={(e) => setNewAddress({...newAddress, state: e.target.value})} required className="form-input" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Postal Code *</label>
                      <input type="text" value={newAddress.postal_code} onChange={(e) => setNewAddress({...newAddress, postal_code: e.target.value})} required className="form-input" />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <input type="text" value={newAddress.country} onChange={(e) => setNewAddress({...newAddress, country: e.target.value})} className="form-input" />
                    </div>
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={newAddress.is_default} onChange={(e) => setNewAddress({...newAddress, is_default: e.target.checked})} />
                      Set as default address
                    </label>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : (editingAddressId ? 'Update Address' : 'Save Address')}</button>
                    <button type="button" className="btn-secondary" onClick={() => { setShowAddressForm(false); setEditingAddressId(null); }}>Cancel</button>
                  </div>
                </form>
              )}

              <div className="addresses-grid">
                {addresses.length === 0 ? (
                  <div className="no-addresses">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="1.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    <p>No addresses saved yet</p>
                    <button className="btn-primary" onClick={() => setShowAddressForm(true)}>Add Your First Address</button>
                  </div>
                ) : (
                  addresses.map(address => (
                    <div key={address.id} className={`address-card ${address.is_default ? 'default' : ''}`}>
                      {address.is_default && <span className="default-badge">Default</span>}
                      <div className="address-details">
                        <p className="address-line">{address.address_line1}</p>
                        {address.address_line2 && <p className="address-line">{address.address_line2}</p>}
                        <p className="address-line">{address.city}, {address.state} {address.postal_code}</p>
                        <p className="address-line">{address.country}</p>
                      </div>
                      <div className="address-actions">
                        {!address.is_default && (
                          <button className="action-btn set-default" onClick={() => handleSetDefaultAddress(address.id)} title="Set as default">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                          </button>
                        )}
                        <button className="action-btn edit" onClick={() => editAddress(address)} title="Edit">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z"/>
                          </svg>
                        </button>
                        <button className="action-btn delete" onClick={() => handleDeleteAddress(address.id)} title="Delete">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── ORDERS ── */}
          {activeTab === 'orders' && (
            <div className="orders-tab">
              <h3>Order History</h3>
              {ordersLoading ? (
                <div className="loading-spinner">Loading orders...</div>
              ) : orders.length === 0 ? (
                <div className="no-orders">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="1.5">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="M8 8h8M8 12h6M8 16h4"/>
                  </svg>
                  <p>No orders yet</p>
                  <Link to="/products" className="btn-primary">Start Shopping</Link>
                </div>
              ) : (
                <div className="orders-list">
                  {orders.map(order => (
                    <div key={order.id} className="order-card">
                      <div className="order-header">
                        <div className="order-info">
                          <span className="order-number">Order #{order.order_number || order.id}</span>
                          <span className="order-date">{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        <div className="order-status">
                          <span className="status-badge" style={{ backgroundColor: getStatusColor(order.order_status || 'pending') }}>
                            {order.order_status || 'pending'}
                          </span>
                        </div>
                      </div>
                      <div className="order-body">
                        <div className="order-items">
                          {order.items.length > 0 ? order.items.map((item, index) => (
                            <div key={item.id || index} className="order-item">
                              <img
                                src={item.image || item.image_url || '/assets/product-placeholder.jpg'}
                                alt={item.product_name}
                                className="item-image"
                                onError={(e) => e.target.src = '/assets/product-placeholder.jpg'}
                              />
                              <div className="item-details">
                                <h4>{item.product_name}</h4>
                                {item.variant_name && <p>Color: {item.variant_name}</p>}
                                {item.size_name    && <p>Size: {item.size_name}</p>}
                                {item.lens_name    && <p>Lens: {item.lens_name}</p>}
                                <p>Qty: {item.quantity} × ₹{parseFloat(item.price || 0).toFixed(2)}</p>
                              </div>
                              <div className="item-total">₹{(parseFloat(item.price || 0) * (item.quantity || 1)).toFixed(2)}</div>
                            </div>
                          )) : (
                            <p className="no-items">No item details available</p>
                          )}
                        </div>
                        <div className="order-total">
                          <span>Total:</span>
                          <span className="total-amount">₹{parseFloat(order.total_amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── PASSWORD ── */}
          {activeTab === 'password' && (
            <div className="password-tab">
              <h3>Change Password</h3>
              <form onSubmit={handlePasswordSubmit} className="password-form">
                <div className="form-group">
                  <label>Current Password</label>
                  <input type="password" value={passwordData.current_password} onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})} required className="form-input" />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input type="password" value={passwordData.new_password} onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})} required className="form-input" />
                  <small>Minimum 6 characters</small>
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input type="password" value={passwordData.confirm_password} onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})} required className="form-input" />
                </div>
                <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Updating...' : 'Change Password'}</button>
              </form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AccountPage;